import { useMemo, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
    ArrowLeft,
    Bell,
    Check,
    Clock,
    Layers,
    Mail,
    MoreVertical,
    Pencil,
    Plus,
    Search,
    Smartphone,
    Trash2,
    X,
} from 'lucide-react'
import { toast } from 'react-toastify'
import { useGetAllDevices } from '../../../../../api/routes/devices'
import { useGetModel } from '../../../../../api/routes/models'
import { useGetModelAttributes } from '../../../../../api/routes/model-attributes'
import {
    Rule,
    RuleComparisonOperator,
    RuleCondition,
    RulePayload,
    useCreateRule,
    useDeleteRule,
    useGetRuleEvents,
    useGetRules,
    useUpdateRule,
} from '../../../../../api/routes/rules'
import { authClient } from '../../../../../lib/auth-client'
import { cn } from '../../../../../lib/utils'
import { Button } from '../../../../../components/ui/button'
import { Checkbox } from '../../../../../components/ui/checkbox'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../../../../components/ui/dialog'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../../../../../components/ui/dropdown-menu'
import { Input } from '../../../../../components/ui/input'
import { Label } from '../../../../../components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../../../components/ui/select'

/* -------------------------------------------------------------------------- */
/*  Types                                                                      */
/* -------------------------------------------------------------------------- */

type Attribute = {
    name: string
    friendlyName?: string | null
    dataType?: string | null
    unit?: string | null
    trueDataValue?: string | null
    falseDataValue?: string | null
}

type MatchType = 'all' | 'any'
type Severity = Rule['severity']

// The trigger is edited as a tree. A leaf is a single reading check ("condition")
// or a "repeated" event (a window count). Groups combine children with all / any
// and can be nested for advanced logic.
type ConditionNode = {
    id: string
    kind: 'condition'
    attribute: string
    operator: RuleComparisonOperator
    value: string
}

type RepeatedNode = {
    id: string
    kind: 'repeated'
    attribute: string
    whereOperator: RuleComparisonOperator
    whereValue: string
    windowSeconds: number
    countOperator: RuleComparisonOperator
    count: string
}

type GroupNode = {
    id: string
    kind: 'group'
    match: MatchType
    children: TriggerNode[]
}

type TriggerNode = ConditionNode | RepeatedNode | GroupNode

type RuleFormState = {
    name: string
    description: string
    severity: Severity
    enabled: boolean

    trigger: GroupNode

    // timing (all stored as seconds)
    triggerForSeconds: number
    resolveForSeconds: number
    remindEnabled: boolean
    cooldownSeconds: number

    // notifications
    notifyOnResolved: boolean
    emailEnabled: boolean
    emailRecipients: string

    // who it applies to
    assignmentType: 'model' | 'devices'
    selectedDeviceIds: string[]
}

const MAX_GROUP_DEPTH = 2 // root group is depth 0; allow nesting two levels deeper

/* -------------------------------------------------------------------------- */
/*  Plain-language vocabulary                                                  */
/* -------------------------------------------------------------------------- */

const operatorLabels: Record<RuleComparisonOperator, string> = {
    '>': 'is above',
    '>=': 'is at least',
    '<': 'is below',
    '<=': 'is at most',
    '==': 'is',
    '!=': 'is not',
    contains: 'contains',
    not_contains: 'does not contain',
    exists: 'has any value',
    not_exists: 'is missing',
}

const countWordLabels: Record<RuleComparisonOperator, string> = {
    '>': 'more than',
    '>=': 'at least',
    '<': 'fewer than',
    '<=': 'at most',
    '==': 'exactly',
    '!=': 'other than',
    contains: 'at least',
    not_contains: 'at least',
    exists: 'at least',
    not_exists: 'at least',
}

const countOperatorOptions: RuleComparisonOperator[] = ['>=', '>', '==', '<=', '<']

const severityMeta: Record<Severity, { label: string; dot: string; soft: string }> = {
    info: { label: 'Info', dot: 'bg-sky-500', soft: 'bg-sky-50 text-sky-700 border-sky-200' },
    warning: { label: 'Warning', dot: 'bg-amber-500', soft: 'bg-amber-50 text-amber-700 border-amber-200' },
    critical: { label: 'Critical', dot: 'bg-rose-500', soft: 'bg-rose-50 text-rose-700 border-rose-200' },
}

const eventMeta: Record<string, { label: string; dot: string }> = {
    triggered: { label: 'Alert', dot: 'bg-rose-500' },
    repeated: { label: 'Reminder', dot: 'bg-amber-500' },
    resolved: { label: 'Cleared', dot: 'bg-emerald-500' },
}

/* -------------------------------------------------------------------------- */
/*  Small helpers                                                              */
/* -------------------------------------------------------------------------- */

const uid = () =>
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2)

const isValueLessOperator = (operator: RuleComparisonOperator) =>
    operator === 'exists' || operator === 'not_exists'

const isBooleanAttr = (attribute?: Attribute) =>
    (attribute?.dataType || '').toLowerCase() === 'boolean'

const attributeLabel = (attribute: Attribute) => {
    const name = attribute.friendlyName || attribute.name
    return attribute.unit ? `${name} (${attribute.unit})` : name
}

const operatorsForAttribute = (attribute?: Attribute): RuleComparisonOperator[] => {
    if (attribute?.name === 'Error') return ['>', '>=', '<', '<=', '==', '!=', 'exists', 'not_exists']
    const dataType = (attribute?.dataType || '').toLowerCase()
    if (dataType === 'boolean') return ['==', '!=', 'exists', 'not_exists']
    if (dataType === 'number') return ['>', '>=', '<', '<=', '==', '!=', 'exists', 'not_exists']
    return ['==', '!=', 'contains', 'not_contains', 'exists', 'not_exists']
}

const clampSeconds = (value: number | string) => {
    const parsed = Math.floor(Number(value) || 0)
    return parsed > 0 ? parsed : 0
}

const splitDuration = (total: number) => {
    const seconds = clampSeconds(total)
    if (seconds === 0) return { amount: 0, unit: 60 }
    for (const unit of [3600, 60, 1]) {
        if (seconds % unit === 0) return { amount: seconds / unit, unit }
    }
    return { amount: seconds, unit: 1 }
}

const humanDuration = (seconds: number) => {
    const total = clampSeconds(seconds)
    if (total === 0) return 'immediately'
    const hours = Math.floor(total / 3600)
    const minutes = Math.floor((total % 3600) / 60)
    const secs = total % 60
    const parts: string[] = []
    if (hours) parts.push(`${hours} hour${hours > 1 ? 's' : ''}`)
    if (minutes) parts.push(`${minutes} minute${minutes > 1 ? 's' : ''}`)
    if (secs) parts.push(`${secs} second${secs > 1 ? 's' : ''}`)
    return parts.join(' ')
}

const shortDuration = (seconds: number) => {
    const total = clampSeconds(seconds)
    if (total === 0) return 'now'
    const hours = Math.floor(total / 3600)
    const minutes = Math.floor((total % 3600) / 60)
    const secs = total % 60
    return [hours && `${hours}h`, minutes && `${minutes}m`, secs && `${secs}s`].filter(Boolean).join(' ')
}

const relativeTime = (input: number | string) => {
    const then = Number(input)
    if (!Number.isFinite(then)) return ''
    const seconds = Math.floor((Date.now() - then) / 1000)
    if (seconds < 60) return 'just now'
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    if (days < 7) return `${days}d ago`
    return new Date(then).toLocaleDateString()
}

const formatValue = (value: unknown) => {
    if (value === undefined || value === null || value === '') return ''
    if (typeof value === 'boolean') return value ? 'on' : 'off'
    if (typeof value === 'object') return JSON.stringify(value)
    return String(value)
}

const phraseComparison = (operator: RuleComparisonOperator, value: string, attribute?: Attribute) => {
    if (operator === 'exists') return 'has any value'
    if (operator === 'not_exists') return 'is missing'
    let display = value
    if (isBooleanAttr(attribute)) {
        display = value === 'true' || value === attribute?.trueDataValue ? 'on' : 'off'
    }
    return `${operatorLabels[operator]} ${display}`.trim()
}

/* -------------------------------------------------------------------------- */
/*  Value parsing for the API payload (matches the server contract)            */
/* -------------------------------------------------------------------------- */

const parseValue = (raw: string, attribute: Attribute | undefined, operator: RuleComparisonOperator) => {
    if (isValueLessOperator(operator)) return undefined
    if (isBooleanAttr(attribute)) {
        if (raw === 'true') return attribute?.trueDataValue || true
        if (raw === 'false') return attribute?.falseDataValue || false
    }
    if ((attribute?.dataType || '').toLowerCase() === 'number' || attribute?.name === 'Error') {
        const parsed = Number(raw)
        return Number.isFinite(parsed) ? parsed : raw
    }
    return raw
}

/* -------------------------------------------------------------------------- */
/*  Tree <-> RuleCondition conversions and immutable tree ops                  */
/* -------------------------------------------------------------------------- */

const makeCondition = (): ConditionNode => ({ id: uid(), kind: 'condition', attribute: 'Error', operator: '>', value: '0' })

const makeRepeated = (): RepeatedNode => ({
    id: uid(),
    kind: 'repeated',
    attribute: 'Error',
    whereOperator: '>',
    whereValue: '0',
    windowSeconds: 600,
    countOperator: '>=',
    count: '3',
})

const makeGroup = (children: TriggerNode[] = [makeCondition()], match: MatchType = 'all'): GroupNode => ({
    id: uid(),
    kind: 'group',
    match,
    children,
})

const conditionToNode = (condition: RuleCondition): TriggerNode => {
    if (condition.type === 'comparison') {
        return {
            id: uid(),
            kind: 'condition',
            attribute: condition.attribute,
            operator: condition.operator,
            value: condition.value != null ? String(condition.value) : '',
        }
    }
    if (condition.type === 'window_count') {
        return {
            id: uid(),
            kind: 'repeated',
            attribute: condition.attribute,
            whereOperator: condition.where.operator,
            whereValue: condition.where.value != null ? String(condition.where.value) : '',
            windowSeconds: clampSeconds(condition.windowSeconds),
            countOperator: condition.countOperator,
            count: String(condition.count),
        }
    }
    return {
        id: uid(),
        kind: 'group',
        match: condition.operator === 'or' ? 'any' : 'all',
        children: condition.conditions.map(conditionToNode),
    }
}

const updateNodeById = (node: TriggerNode, id: string, updater: (node: TriggerNode) => TriggerNode): TriggerNode => {
    if (node.id === id) return updater(node)
    if (node.kind === 'group') {
        return { ...node, children: node.children.map((child) => updateNodeById(child, id, updater)) }
    }
    return node
}

const removeNodeById = (group: GroupNode, id: string): GroupNode => ({
    ...group,
    children: group.children
        .filter((child) => child.id !== id)
        .map((child) => (child.kind === 'group' ? removeNodeById(child, id) : child)),
})

const countLeaves = (node: TriggerNode): number =>
    node.kind === 'group' ? node.children.reduce((sum, child) => sum + countLeaves(child), 0) : 1

const conditionToSentence = (condition: RuleCondition | null, attributesByName: Map<string, Attribute>): string => {
    if (!condition) return 'a condition is met'

    if (condition.type === 'comparison') {
        const attribute = attributesByName.get(condition.attribute)
        const name = attribute ? attributeLabel(attribute) : condition.attribute
        const value = condition.value != null ? formatValue(condition.value) : ''
        return `${name} ${phraseComparison(condition.operator, value, attribute)}`
    }

    if (condition.type === 'window_count') {
        const attribute = attributesByName.get(condition.attribute)
        const name = attribute ? attributeLabel(attribute) : condition.attribute
        const value = condition.where.value != null ? formatValue(condition.where.value) : ''
        return `${name} ${phraseComparison(condition.where.operator, value, attribute)} ${countWordLabels[condition.countOperator]} ${condition.count} times in ${humanDuration(condition.windowSeconds)}`
    }

    const joiner = condition.operator === 'or' ? ' or ' : ' and '
    const parts = condition.conditions.map((child) => {
        const sentence = conditionToSentence(child, attributesByName)
        return child.type === 'group' ? `(${sentence})` : sentence
    })
    if (parts.length <= 3) return parts.join(joiner)
    return `${parts.slice(0, 3).join(joiner)}${joiner}${parts.length - 3} more`
}

/* -------------------------------------------------------------------------- */
/*  Form defaults + conversions                                               */
/* -------------------------------------------------------------------------- */

const emptyForm = (): RuleFormState => ({
    name: '',
    description: '',
    severity: 'warning',
    enabled: true,
    trigger: makeGroup(),
    triggerForSeconds: 0,
    resolveForSeconds: 0,
    remindEnabled: true,
    cooldownSeconds: 3600,
    notifyOnResolved: true,
    emailEnabled: false,
    emailRecipients: '',
    assignmentType: 'model',
    selectedDeviceIds: [],
})

const ruleToForm = (rule: Rule): RuleFormState => {
    const form = emptyForm()
    form.name = rule.name
    form.description = rule.description || ''
    form.severity = rule.severity
    form.enabled = rule.enabled
    form.triggerForSeconds = clampSeconds(rule.triggerForSeconds)
    form.resolveForSeconds = clampSeconds(rule.resolveForSeconds)
    form.cooldownSeconds = clampSeconds(rule.cooldownSeconds)
    form.remindEnabled = clampSeconds(rule.cooldownSeconds) > 0
    form.notifyOnResolved = rule.notificationConfig?.notifyOnResolved === true
    form.emailEnabled = rule.notificationConfig?.channels?.includes('email') || false
    form.emailRecipients =
        rule.notificationConfig?.recipients
            ?.map((recipient: any) => (typeof recipient === 'string' ? '' : recipient.email || ''))
            .filter(Boolean)
            .join(', ') || ''
    form.assignmentType = rule.assignment?.type === 'devices' ? 'devices' : 'model'
    form.selectedDeviceIds = rule.assignment?.deviceIds || []

    if (rule.triggerCondition) {
        const node = conditionToNode(rule.triggerCondition)
        form.trigger = node.kind === 'group' ? node : makeGroup([node])
    }

    return form
}

// Rebuild a full payload from an existing rule (used for the inline on/off toggle).
const ruleToPayload = (rule: Rule, overrides: Partial<RulePayload>): RulePayload => ({
    modelId: rule.modelId,
    name: rule.name,
    description: rule.description,
    severity: rule.severity,
    enabled: rule.enabled,
    triggerCondition: rule.triggerCondition,
    resolveCondition: rule.resolveCondition,
    triggerForSeconds: rule.triggerForSeconds,
    resolveForSeconds: rule.resolveForSeconds,
    cooldownSeconds: rule.cooldownSeconds,
    persistentAlert: rule.persistentAlert,
    notificationConfig: rule.notificationConfig,
    assignment:
        rule.assignment?.type === 'devices'
            ? { type: 'devices', deviceIds: rule.assignment.deviceIds || [] }
            : { type: 'model' },
    ...overrides,
})

/* -------------------------------------------------------------------------- */
/*  Reusable presentational pieces                                            */
/* -------------------------------------------------------------------------- */

const Switch = ({
    checked,
    onChange,
    disabled,
    id,
}: {
    checked: boolean
    onChange: (next: boolean) => void
    disabled?: boolean
    id?: string
}) => (
    <button
        type="button"
        role="switch"
        id={id}
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
            'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            checked ? 'bg-primary' : 'bg-input'
        )}
    >
        <span
            className={cn(
                'inline-block h-4 w-4 transform rounded-full bg-background shadow-sm transition-transform',
                checked ? 'translate-x-[18px]' : 'translate-x-0.5'
            )}
        />
    </button>
)

const Segmented = <T extends string>({
    value,
    onChange,
    options,
    size = 'md',
    className,
}: {
    value: T
    onChange: (next: T) => void
    options: { value: T; label: ReactNode }[]
    size?: 'sm' | 'md'
    className?: string
}) => (
    <div className={cn('inline-flex items-center gap-0.5 rounded-lg border bg-muted/60 p-0.5', className)}>
        {options.map((option) => (
            <button
                key={option.value}
                type="button"
                onClick={() => onChange(option.value)}
                className={cn(
                    'rounded-md font-medium transition-colors',
                    size === 'sm' ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm',
                    value === option.value
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                )}
            >
                {option.label}
            </button>
        ))}
    </div>
)

const Section = ({
    step,
    title,
    hint,
    children,
}: {
    step: number
    title: string
    hint: string
    children: ReactNode
}) => (
    <section className="border-b px-6 py-5 last:border-b-0">
        <div className="mb-4 flex items-start gap-3">
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
                {step}
            </div>
            <div>
                <h3 className="text-sm font-semibold text-foreground">{title}</h3>
                <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>
            </div>
        </div>
        <div className="sm:pl-9">{children}</div>
    </section>
)

const SettingRow = ({ title, hint, control }: { title: string; hint?: ReactNode; control: ReactNode }) => (
    <div className="flex items-center justify-between gap-4 py-2.5">
        <div className="min-w-0">
            <div className="text-sm text-foreground">{title}</div>
            {hint && <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>}
        </div>
        <div className="shrink-0">{control}</div>
    </div>
)

const DURATION_UNITS = [
    { label: 'seconds', value: 1 },
    { label: 'minutes', value: 60 },
    { label: 'hours', value: 3600 },
]

const DurationField = ({
    value,
    onChange,
    disabled,
}: {
    value: number
    onChange: (seconds: number) => void
    disabled?: boolean
}) => {
    const { amount, unit } = splitDuration(value)
    return (
        <div className="flex items-center gap-2">
            <Input
                type="number"
                min={0}
                disabled={disabled}
                value={String(amount)}
                onChange={(event) => onChange(Math.max(0, Math.floor(Number(event.target.value) || 0)) * unit)}
                className="w-20"
            />
            <Select value={String(unit)} onValueChange={(next) => onChange(amount * Number(next))} disabled={disabled}>
                <SelectTrigger className="w-[120px]">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {DURATION_UNITS.map((option) => (
                        <SelectItem key={option.value} value={String(option.value)}>
                            {option.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    )
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                       */
/* -------------------------------------------------------------------------- */

export const ModelRulesRoute = () => {
    const { id: modelId } = useParams<{ id: string }>()
    const { data: session } = authClient.useSession()
    const { data: model, isLoading: modelLoading } = useGetModel(modelId!)
    const { data: attributesRaw } = useGetModelAttributes(modelId!)
    const { data: devicesRaw } = useGetAllDevices()
    const { data: rules, isLoading: rulesLoading } = useGetRules(modelId!)
    const { data: events } = useGetRuleEvents(modelId!)

    const createRule = useCreateRule()
    const updateRule = useUpdateRule()
    const deleteRule = useDeleteRule()

    const [dialogOpen, setDialogOpen] = useState(false)
    const [editingRule, setEditingRule] = useState<Rule | null>(null)
    const [form, setForm] = useState<RuleFormState>(() => emptyForm())
    const [deviceSearch, setDeviceSearch] = useState('')
    const [deletingRule, setDeletingRule] = useState<Rule | null>(null)

    const attributes = useMemo<Attribute[]>(() => {
        const modelAttributes = Array.isArray(attributesRaw) ? attributesRaw : []
        return [
            { name: 'Error', friendlyName: 'Error code', dataType: 'number', unit: null },
            ...modelAttributes.map((attribute: any) => ({
                name: attribute.name,
                friendlyName: attribute.friendlyName,
                dataType: attribute.dataType,
                unit: attribute.unit,
                trueDataValue: attribute.trueDataValue,
                falseDataValue: attribute.falseDataValue,
            })),
        ]
    }, [attributesRaw])

    const attributesByName = useMemo(
        () => new Map(attributes.map((attribute) => [attribute.name, attribute])),
        [attributes]
    )

    const modelDevices = useMemo(() => {
        const devices = Array.isArray(devicesRaw) ? devicesRaw : []
        return devices.filter((device: any) => device.model?.id === modelId)
    }, [devicesRaw, modelId])

    const filteredDevices = useMemo(() => {
        const query = deviceSearch.trim().toLowerCase()
        if (!query) return modelDevices
        return modelDevices.filter((device: any) => (device.name || '').toLowerCase().includes(query))
    }, [modelDevices, deviceSearch])

    const updateForm = (patch: Partial<RuleFormState>) => setForm((current) => ({ ...current, ...patch }))

    /* ---- trigger tree editing ---- */

    const setTrigger = (updater: (tree: GroupNode) => GroupNode) =>
        setForm((current) => ({ ...current, trigger: updater(current.trigger) }))

    const patchNode = (id: string, patch: Record<string, unknown>) =>
        setTrigger((tree) => updateNodeById(tree, id, (node) => ({ ...node, ...patch }) as TriggerNode) as GroupNode)

    const addChild = (groupId: string, child: TriggerNode) =>
        setTrigger(
            (tree) =>
                updateNodeById(tree, groupId, (node) =>
                    node.kind === 'group' ? { ...node, children: [...node.children, child] } : node
                ) as GroupNode
        )

    const removeNode = (id: string) => setTrigger((tree) => removeNodeById(tree, id))

    const changeNodeAttribute = (id: string, attributeName: string) => {
        const attribute = attributesByName.get(attributeName)
        const allowed = operatorsForAttribute(attribute)
        const normalizeBool = (value: string) =>
            isBooleanAttr(attribute) ? (value === 'true' || value === attribute?.trueDataValue ? 'true' : 'false') : value
        setTrigger(
            (tree) =>
                updateNodeById(tree, id, (node) => {
                    if (node.kind === 'condition') {
                        return {
                            ...node,
                            attribute: attributeName,
                            operator: allowed.includes(node.operator) ? node.operator : allowed[0],
                            value: normalizeBool(node.value),
                        }
                    }
                    if (node.kind === 'repeated') {
                        return {
                            ...node,
                            attribute: attributeName,
                            whereOperator: allowed.includes(node.whereOperator) ? node.whereOperator : allowed[0],
                            whereValue: normalizeBool(node.whereValue),
                        }
                    }
                    return node
                }) as GroupNode
        )
    }

    const nodeToCondition = (node: TriggerNode): RuleCondition | null => {
        if (node.kind === 'condition') {
            if (!node.attribute) return null
            const attribute = attributesByName.get(node.attribute)
            const value = parseValue(node.value, attribute, node.operator)
            return {
                type: 'comparison',
                attribute: node.attribute,
                operator: node.operator,
                ...(value !== undefined ? { value } : {}),
            }
        }
        if (node.kind === 'repeated') {
            if (!node.attribute) return null
            const attribute = attributesByName.get(node.attribute)
            return {
                type: 'window_count',
                attribute: node.attribute,
                where: {
                    operator: node.whereOperator,
                    ...(!isValueLessOperator(node.whereOperator)
                        ? { value: parseValue(node.whereValue, attribute, node.whereOperator) }
                        : {}),
                },
                windowSeconds: Math.max(1, clampSeconds(node.windowSeconds)),
                countOperator: node.countOperator,
                count: Math.max(1, Number(node.count) || 1),
            }
        }
        const children = node.children
            .map(nodeToCondition)
            .filter((child): child is RuleCondition => child !== null)
        if (children.length === 0) return null
        if (children.length === 1) return children[0]
        return { type: 'group', operator: node.match === 'any' ? 'or' : 'and', conditions: children }
    }

    const previewCondition = nodeToCondition(form.trigger)
    const summary = conditionToSentence(previewCondition, attributesByName)
    const canRemove = countLeaves(form.trigger) > 1

    const openCreate = () => {
        setEditingRule(null)
        setForm(emptyForm())
        setDeviceSearch('')
        setDialogOpen(true)
    }

    const openEdit = (rule: Rule) => {
        setEditingRule(rule)
        setForm(ruleToForm(rule))
        setDeviceSearch('')
        setDialogOpen(true)
    }

    const handleSave = async () => {
        try {
            const name = form.name.trim()
            if (!name) {
                toast.error('Give your alert a name')
                return
            }
            const triggerCondition = nodeToCondition(form.trigger)
            if (!triggerCondition) {
                toast.error('Add at least one condition')
                return
            }
            if (form.assignmentType === 'devices' && form.selectedDeviceIds.length === 0) {
                toast.error('Select at least one device')
                return
            }

            const userId = (session?.user as any)?.id
            const emails = form.emailRecipients
                .split(',')
                .map((email) => email.trim())
                .filter(Boolean)

            const payload: RulePayload = {
                modelId: modelId!,
                name,
                description: form.description.trim() || null,
                severity: form.severity,
                enabled: form.enabled,
                triggerCondition,
                resolveCondition: null,
                triggerForSeconds: clampSeconds(form.triggerForSeconds),
                resolveForSeconds: clampSeconds(form.resolveForSeconds),
                cooldownSeconds: form.remindEnabled ? clampSeconds(form.cooldownSeconds) : 0,
                persistentAlert: false,
                notificationConfig: {
                    channels: form.emailEnabled ? ['in_app', 'email'] : ['in_app'],
                    recipients: [
                        ...(userId ? [{ type: 'user' as const, userId }] : []),
                        ...emails.map((email) => ({ type: 'email' as const, email })),
                    ],
                    notifyOnResolved: form.notifyOnResolved,
                },
                assignment:
                    form.assignmentType === 'devices'
                        ? { type: 'devices', deviceIds: form.selectedDeviceIds }
                        : { type: 'model' },
            }

            if (editingRule) {
                await updateRule.mutateAsync({ id: editingRule.id, data: payload })
                toast.success('Alert updated')
            } else {
                await createRule.mutateAsync(payload)
                toast.success('Alert created')
            }

            setDialogOpen(false)
            setEditingRule(null)
        } catch (error: any) {
            toast.error(error?.response?.data?.message || error.message || 'Could not save the alert')
        }
    }

    const handleToggleEnabled = async (rule: Rule, next: boolean) => {
        try {
            await updateRule.mutateAsync({ id: rule.id, data: ruleToPayload(rule, { enabled: next }) })
        } catch {
            toast.error('Could not update the alert')
        }
    }

    const confirmDelete = async () => {
        if (!deletingRule) return
        try {
            await deleteRule.mutateAsync({ id: deletingRule.id, modelId: modelId! })
            toast.success('Alert deleted')
            setDeletingRule(null)
        } catch {
            toast.error('Could not delete the alert')
        }
    }

    /* ---- trigger tree rendering (plain render fns, not components, to keep focus) ---- */

    const attributeSelect = (value: string, onChange: (next: string) => void) => (
        <Select value={value} onValueChange={onChange}>
            <SelectTrigger>
                <SelectValue placeholder="Choose a reading" />
            </SelectTrigger>
            <SelectContent>
                {attributes.map((option) => (
                    <SelectItem key={option.name} value={option.name}>
                        {attributeLabel(option)}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    )

    const operatorSelect = (
        attribute: Attribute | undefined,
        value: RuleComparisonOperator,
        onChange: (next: RuleComparisonOperator) => void
    ) => (
        <Select value={value} onValueChange={(next: RuleComparisonOperator) => onChange(next)}>
            <SelectTrigger>
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                {operatorsForAttribute(attribute).map((operator) => (
                    <SelectItem key={operator} value={operator}>
                        {operatorLabels[operator]}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    )

    const valueControl = (
        attribute: Attribute | undefined,
        operator: RuleComparisonOperator,
        value: string,
        onChange: (next: string) => void
    ) => {
        if (isValueLessOperator(operator)) {
            return (
                <div className="flex h-10 items-center rounded-md border border-dashed px-3 text-sm text-muted-foreground">
                    no value needed
                </div>
            )
        }
        if (isBooleanAttr(attribute)) {
            return (
                <Select value={value === 'true' || value === attribute?.trueDataValue ? 'true' : 'false'} onValueChange={onChange}>
                    <SelectTrigger>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="true">on</SelectItem>
                        <SelectItem value="false">off</SelectItem>
                    </SelectContent>
                </Select>
            )
        }
        return <Input placeholder="value" value={value} onChange={(event) => onChange(event.target.value)} />
    }

    const removeButton = (id: string, disabled?: boolean) => (
        <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0 text-muted-foreground hover:text-foreground"
            disabled={disabled}
            onClick={() => removeNode(id)}
        >
            <X className="h-4 w-4" />
        </Button>
    )

    const renderCondition = (node: ConditionNode) => {
        const attribute = attributesByName.get(node.attribute)
        return (
            <div className="grid items-center gap-2 sm:grid-cols-[minmax(0,1fr)_140px_minmax(0,1fr)_auto]">
                {attributeSelect(node.attribute, (next) => changeNodeAttribute(node.id, next))}
                {operatorSelect(attribute, node.operator, (next) => patchNode(node.id, { operator: next }))}
                {valueControl(attribute, node.operator, node.value, (next) => patchNode(node.id, { value: next }))}
                {removeButton(node.id, !canRemove)}
            </div>
        )
    }

    const renderRepeated = (node: RepeatedNode) => {
        const attribute = attributesByName.get(node.attribute)
        return (
            <div className="rounded-lg border bg-background p-3">
                <div className="mb-2 flex items-center justify-between">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Repeated event
                    </span>
                    {removeButton(node.id, !canRemove)}
                </div>
                <div className="grid items-center gap-2 sm:grid-cols-[minmax(0,1fr)_140px_minmax(0,1fr)]">
                    {attributeSelect(node.attribute, (next) => changeNodeAttribute(node.id, next))}
                    {operatorSelect(attribute, node.whereOperator, (next) => patchNode(node.id, { whereOperator: next }))}
                    {valueControl(attribute, node.whereOperator, node.whereValue, (next) => patchNode(node.id, { whereValue: next }))}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                    <span>happens</span>
                    <Select
                        value={node.countOperator}
                        onValueChange={(next: RuleComparisonOperator) => patchNode(node.id, { countOperator: next })}
                    >
                        <SelectTrigger className="w-[130px]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {countOperatorOptions.map((operator) => (
                                <SelectItem key={operator} value={operator}>
                                    {countWordLabels[operator]}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Input
                        type="number"
                        min={1}
                        value={node.count}
                        onChange={(event) => patchNode(node.id, { count: event.target.value })}
                        className="w-16"
                    />
                    <span>times within</span>
                    <DurationField value={node.windowSeconds} onChange={(seconds) => patchNode(node.id, { windowSeconds: seconds })} />
                </div>
            </div>
        )
    }

    const renderNode = (node: TriggerNode, depth: number): ReactNode => {
        if (node.kind === 'group') return renderGroup(node, depth)
        if (node.kind === 'repeated') return renderRepeated(node)
        return renderCondition(node)
    }

    const renderGroup = (group: GroupNode, depth: number): ReactNode => {
        const isRoot = depth === 0
        const matchControl = (
            <Segmented<MatchType>
                size="sm"
                value={group.match}
                onChange={(match) => patchNode(group.id, { match })}
                options={[
                    { value: 'all', label: 'all' },
                    { value: 'any', label: 'any' },
                ]}
            />
        )

        const children = (
            <div className="space-y-2">
                {isRoot && group.children.length > 1 && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>Alert when</span>
                        {matchControl}
                        <span>of the following are true</span>
                    </div>
                )}
                {group.children.map((child) => (
                    <div key={child.id}>{renderNode(child, depth + 1)}</div>
                ))}
                <div className="flex flex-wrap items-center gap-2 pt-0.5">
                    <Button type="button" variant="outline" size="sm" onClick={() => addChild(group.id, makeCondition())}>
                        <Plus className="mr-1 h-3.5 w-3.5" />
                        Condition
                    </Button>
                    {depth < MAX_GROUP_DEPTH && (
                        <Button type="button" variant="outline" size="sm" onClick={() => addChild(group.id, makeGroup())}>
                            <Layers className="mr-1 h-3.5 w-3.5" />
                            Group
                        </Button>
                    )}
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground"
                        onClick={() => addChild(group.id, makeRepeated())}
                    >
                        <Plus className="mr-1 h-3.5 w-3.5" />
                        Repeated event
                    </Button>
                </div>
            </div>
        )

        if (isRoot) return children

        return (
            <div className="rounded-lg border-l-2 border-primary/30 bg-muted/30 p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>Match</span>
                        {matchControl}
                    </div>
                    {removeButton(group.id)}
                </div>
                {children}
            </div>
        )
    }

    if (modelLoading) {
        return (
            <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32 text-sm text-muted-foreground">
                Loading…
            </div>
        )
    }

    if (!model) {
        return (
            <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32 text-sm text-muted-foreground">
                Model not found.
            </div>
        )
    }

    const recentEvents = Array.isArray(events) ? events.slice(0, 8) : []

    return (
        <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
            {/* Header */}
            <div className="mb-6">
                <Link
                    to={`/models/${modelId}`}
                    className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Back to {model.name}
                </Link>
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Alerts</h1>
                        <p className="mt-1 text-sm text-muted-foreground">
                            Get notified the moment a {model.name} needs attention.
                        </p>
                    </div>
                    {rules && rules.length > 0 && (
                        <Button onClick={openCreate}>
                            <Plus className="mr-1.5 h-4 w-4" />
                            New alert
                        </Button>
                    )}
                </div>
            </div>

            {/* Rule list */}
            {rulesLoading ? (
                <div className="text-sm text-muted-foreground">Loading…</div>
            ) : !rules || rules.length === 0 ? (
                <div className="rounded-xl border border-dashed bg-card p-12 text-center">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                        <Bell className="h-6 w-6 text-muted-foreground" />
                    </div>
                    <h3 className="mt-4 text-sm font-semibold text-foreground">No alerts yet</h3>
                    <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
                        Set up your first alert and we'll let you know when a {model.name} reads something you care
                        about — like a low battery or a fault.
                    </p>
                    <Button className="mt-5" onClick={openCreate}>
                        <Plus className="mr-1.5 h-4 w-4" />
                        Create your first alert
                    </Button>
                </div>
            ) : (
                <div className="grid gap-3">
                    {rules.map((rule) => {
                        const meta = severityMeta[rule.severity]
                        const deviceCount =
                            rule.assignment?.type === 'devices' ? rule.assignment.deviceIds?.length || 0 : null
                        const emailOn = rule.notificationConfig?.channels?.includes('email')
                        return (
                            <div
                                key={rule.id}
                                className={cn(
                                    'rounded-xl border bg-card p-4 transition-all hover:shadow-sm',
                                    rule.enabled ? 'hover:border-primary/30' : 'opacity-75'
                                )}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex min-w-0 items-start gap-3">
                                        <span
                                            className={cn('mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full', meta.dot)}
                                            title={meta.label}
                                        />
                                        <div className="min-w-0">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <h3 className="text-sm font-semibold text-foreground">{rule.name}</h3>
                                                <span className={cn('rounded-full border px-2 py-0.5 text-[11px] font-medium', meta.soft)}>
                                                    {meta.label}
                                                </span>
                                                {!rule.enabled && (
                                                    <span className="rounded-full border bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                                                        Off
                                                    </span>
                                                )}
                                            </div>
                                            {rule.description && (
                                                <p className="mt-1 text-xs text-muted-foreground">{rule.description}</p>
                                            )}
                                            <p className="mt-2 text-sm text-foreground/90 line-clamp-2">
                                                <span className="text-muted-foreground">Notifies when </span>
                                                {conditionToSentence(rule.triggerCondition, attributesByName)}.
                                            </p>
                                            <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                                                <span className="inline-flex items-center gap-1.5">
                                                    <Smartphone className="h-3.5 w-3.5" />
                                                    {deviceCount === null
                                                        ? 'All devices'
                                                        : `${deviceCount} device${deviceCount === 1 ? '' : 's'}`}
                                                </span>
                                                <span className="inline-flex items-center gap-1.5">
                                                    {emailOn ? <Mail className="h-3.5 w-3.5" /> : <Bell className="h-3.5 w-3.5" />}
                                                    {emailOn ? 'In-app + email' : 'In-app'}
                                                </span>
                                                {rule.triggerForSeconds > 0 && (
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <Clock className="h-3.5 w-3.5" />
                                                        Held {shortDuration(rule.triggerForSeconds)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex shrink-0 items-center gap-1">
                                        <Switch checked={rule.enabled} onChange={(next) => handleToggleEnabled(rule, next)} />
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                                    <MoreVertical className="h-4 w-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem onClick={() => openEdit(rule)}>
                                                    <Pencil className="mr-2 h-4 w-4" />
                                                    Edit
                                                </DropdownMenuItem>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem
                                                    className="text-destructive focus:text-destructive"
                                                    onClick={() => setDeletingRule(rule)}
                                                >
                                                    <Trash2 className="mr-2 h-4 w-4" />
                                                    Delete
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}

            {/* Recent activity */}
            <div className="mt-10">
                <h2 className="mb-3 text-sm font-semibold text-foreground">Recent activity</h2>
                {recentEvents.length === 0 ? (
                    <div className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground">
                        Nothing has triggered yet. Activity will show here once an alert fires.
                    </div>
                ) : (
                    <div className="divide-y rounded-xl border bg-card">
                        {recentEvents.map((event: any) => {
                            const meta = eventMeta[event.eventType] || eventMeta.triggered
                            return (
                                <div key={event.id} className="flex items-center justify-between gap-4 px-4 py-3">
                                    <div className="flex min-w-0 items-center gap-3">
                                        <span className={cn('h-2 w-2 shrink-0 rounded-full', meta.dot)} />
                                        <div className="min-w-0">
                                            <p className="truncate text-sm text-foreground">{event.message}</p>
                                            <p className="text-xs text-muted-foreground">
                                                {event.device?.name || 'Device'} · {relativeTime(event.occurredAt)}
                                            </p>
                                        </div>
                                    </div>
                                    <span className="shrink-0 text-xs font-medium text-muted-foreground">{meta.label}</span>
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>

            {/* Builder dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col gap-0 overflow-hidden p-0">
                    <DialogHeader className="shrink-0 border-b px-6 py-4">
                        <DialogTitle>{editingRule ? 'Edit alert' : 'New alert'}</DialogTitle>
                        <DialogDescription>Tell us what to watch for on your {model.name} devices.</DialogDescription>
                    </DialogHeader>

                    <div className="min-h-0 flex-1 overflow-y-auto">
                        {/* 1. Basics */}
                        <Section step={1} title="Name this alert" hint="A short name your team will recognise.">
                            <div className="space-y-4">
                                <div className="space-y-1.5">
                                    <Label htmlFor="rule-name">Alert name</Label>
                                    <Input
                                        id="rule-name"
                                        placeholder="e.g. Low battery"
                                        value={form.name}
                                        onChange={(event) => updateForm({ name: event.target.value })}
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label>How important is it?</Label>
                                    <div>
                                        <Segmented<Severity>
                                            value={form.severity}
                                            onChange={(severity) => updateForm({ severity })}
                                            options={(['info', 'warning', 'critical'] as Severity[]).map((value) => ({
                                                value,
                                                label: (
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <span className={cn('h-2 w-2 rounded-full', severityMeta[value].dot)} />
                                                        {severityMeta[value].label}
                                                    </span>
                                                ),
                                            }))}
                                        />
                                    </div>
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="rule-description">Note (optional)</Label>
                                    <Input
                                        id="rule-description"
                                        placeholder="Add context for whoever gets the alert"
                                        value={form.description}
                                        onChange={(event) => updateForm({ description: event.target.value })}
                                    />
                                </div>
                            </div>
                        </Section>

                        {/* 2. Trigger */}
                        <Section
                            step={2}
                            title="When should it alert?"
                            hint="Add conditions and combine them with “all” or “any”. Use groups to nest more advanced logic."
                        >
                            {renderNode(form.trigger, 0)}
                        </Section>

                        {/* 3. Who */}
                        <Section step={3} title="Which devices?" hint="Apply this to the whole fleet or specific units.">
                            <Segmented<'model' | 'devices'>
                                value={form.assignmentType}
                                onChange={(assignmentType) => updateForm({ assignmentType })}
                                options={[
                                    { value: 'model', label: `All ${model.name} devices` },
                                    { value: 'devices', label: 'Specific devices' },
                                ]}
                            />

                            {form.assignmentType === 'devices' && (
                                <div className="mt-3 rounded-lg border">
                                    <div className="flex items-center gap-2 border-b px-3 py-2">
                                        <Search className="h-4 w-4 text-muted-foreground" />
                                        <input
                                            value={deviceSearch}
                                            onChange={(event) => setDeviceSearch(event.target.value)}
                                            placeholder="Search devices…"
                                            className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                                        />
                                        <span className="shrink-0 text-xs text-muted-foreground">
                                            {form.selectedDeviceIds.length} selected
                                        </span>
                                    </div>
                                    <div className="max-h-44 overflow-y-auto p-1">
                                        {filteredDevices.length === 0 ? (
                                            <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                                                No devices found.
                                            </div>
                                        ) : (
                                            filteredDevices.map((device: any) => {
                                                const checked = form.selectedDeviceIds.includes(device.id)
                                                return (
                                                    <label
                                                        key={device.id}
                                                        className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-sm hover:bg-muted/60"
                                                    >
                                                        <Checkbox
                                                            checked={checked}
                                                            onCheckedChange={(value) =>
                                                                setForm((current) => ({
                                                                    ...current,
                                                                    selectedDeviceIds:
                                                                        value === true
                                                                            ? [...current.selectedDeviceIds, device.id]
                                                                            : current.selectedDeviceIds.filter((deviceId) => deviceId !== device.id),
                                                                }))
                                                            }
                                                        />
                                                        <span className="truncate">{device.name}</span>
                                                    </label>
                                                )
                                            })
                                        )}
                                    </div>
                                </div>
                            )}
                        </Section>

                        {/* 4. Notifications */}
                        <Section
                            step={4}
                            title="How should we tell you?"
                            hint="In-app alerts are always on. Add email if you like."
                        >
                            <div className="divide-y">
                                <SettingRow
                                    title="In-app notification"
                                    hint="Shows up in Trakrr for your team"
                                    control={
                                        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600">
                                            <Check className="h-4 w-4" /> Always on
                                        </span>
                                    }
                                />
                                <SettingRow
                                    title="Email"
                                    hint="Send a copy to one or more inboxes"
                                    control={<Switch checked={form.emailEnabled} onChange={(value) => updateForm({ emailEnabled: value })} />}
                                />
                                {form.emailEnabled && (
                                    <div className="space-y-1.5 py-2.5">
                                        <Label htmlFor="rule-emails" className="text-xs text-muted-foreground">
                                            Email addresses
                                        </Label>
                                        <Input
                                            id="rule-emails"
                                            placeholder="alex@company.com, ops@company.com"
                                            value={form.emailRecipients}
                                            onChange={(event) => updateForm({ emailRecipients: event.target.value })}
                                        />
                                        <p className="text-xs text-muted-foreground">Separate multiple addresses with commas.</p>
                                    </div>
                                )}
                                <SettingRow
                                    title="Tell me when it clears"
                                    hint="Send a follow-up once things are back to normal"
                                    control={<Switch checked={form.notifyOnResolved} onChange={(value) => updateForm({ notifyOnResolved: value })} />}
                                />
                            </div>
                        </Section>

                        {/* 5. Timing */}
                        <Section step={5} title="Fine-tune the timing" hint="Optional — sensible defaults already apply.">
                            <div className="divide-y">
                                <SettingRow
                                    title="Wait before alerting"
                                    hint={
                                        form.triggerForSeconds > 0
                                            ? `Only alert if it stays true for ${humanDuration(form.triggerForSeconds)}`
                                            : 'Alert as soon as it happens'
                                    }
                                    control={
                                        <DurationField
                                            value={form.triggerForSeconds}
                                            onChange={(seconds) => updateForm({ triggerForSeconds: seconds })}
                                        />
                                    }
                                />
                                <SettingRow
                                    title="Wait before clearing"
                                    hint={
                                        form.resolveForSeconds > 0
                                            ? `Stay alerted until normal for ${humanDuration(form.resolveForSeconds)}`
                                            : 'Clear as soon as it returns to normal'
                                    }
                                    control={
                                        <DurationField
                                            value={form.resolveForSeconds}
                                            onChange={(seconds) => updateForm({ resolveForSeconds: seconds })}
                                        />
                                    }
                                />
                                <SettingRow
                                    title="Remind me while it's active"
                                    hint={
                                        form.remindEnabled
                                            ? `Repeat the alert every ${humanDuration(form.cooldownSeconds)}`
                                            : 'Notify once, then stay quiet until it clears'
                                    }
                                    control={<Switch checked={form.remindEnabled} onChange={(value) => updateForm({ remindEnabled: value })} />}
                                />
                                {form.remindEnabled && (
                                    <SettingRow
                                        title="Remind every"
                                        control={
                                            <DurationField
                                                value={form.cooldownSeconds}
                                                onChange={(seconds) => updateForm({ cooldownSeconds: seconds })}
                                            />
                                        }
                                    />
                                )}
                            </div>
                        </Section>
                    </div>

                    {/* Live summary */}
                    <div className="shrink-0 border-t bg-muted/40 px-6 py-3">
                        <div className="flex items-start gap-2.5">
                            <Bell className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                            <p className="text-sm text-foreground">
                                <span className="text-muted-foreground">Notify when </span>
                                {summary}
                                <span className="text-muted-foreground">
                                    {form.triggerForSeconds > 0 ? `, held for ${humanDuration(form.triggerForSeconds)}` : ''}
                                    {' · '}
                                    {form.assignmentType === 'devices'
                                        ? `${form.selectedDeviceIds.length} device${form.selectedDeviceIds.length === 1 ? '' : 's'}`
                                        : `all ${model.name} devices`}
                                    {' · '}
                                    {form.emailEnabled ? 'in-app + email' : 'in-app'}
                                </span>
                            </p>
                        </div>
                    </div>

                    <DialogFooter className="shrink-0 border-t px-6 py-4">
                        <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button type="button" onClick={handleSave} disabled={createRule.isPending || updateRule.isPending}>
                            {editingRule ? 'Save alert' : 'Create alert'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirmation */}
            <Dialog open={!!deletingRule} onOpenChange={(open) => !open && setDeletingRule(null)}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Delete this alert?</DialogTitle>
                        <DialogDescription>
                            “{deletingRule?.name}” will stop notifying anyone. This can't be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setDeletingRule(null)}>
                            Keep it
                        </Button>
                        <Button type="button" variant="destructive" onClick={confirmDelete} disabled={deleteRule.isPending}>
                            Delete alert
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
